import React from "react";
import { Mail, Phone, MapPin, Globe, ArrowRight } from "lucide-react";
import {
  FaLinkedinIn,
  FaTwitter,
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import logo from "../Images/logo.svg";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#03152d] text-white relative overflow-hidden">
      {/* Ambient glow blobs */}
      <div className="absolute -top-40 -left-40 w-[400px] h-[400px] rounded-full bg-[#355dff] opacity-10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-[#355dff] opacity-10 blur-3xl pointer-events-none" />

      {/* CTA Banner */}
      <div className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold leading-snug">
              Ready to Accelerate Your{" "}
              <span className="text-[#4da3ff]">Digital Innovation?</span>
            </h2>

            <p className="text-gray-400 mt-2 text-sm md:text-base">
              Let's build something great together. Reach out today.
            </p>
          </div>

          <Link
            to="/contact"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#355dff] hover:bg-[#2448d8] text-white font-semibold px-6 py-3 rounded-xl transition duration-300 shadow-lg hover:shadow-[#355dff]/30 hover:shadow-xl"
          >
            Get In Touch
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand */}
        <div className="lg:col-span-1">
          <Link to="/" className="flex items-center gap-2 mb-4 w-fit">
            <img
            src={logo}
            alt="Orryworx Logo"
            className="w-12 h-12 object-contain brightness-0 invert"
          />
          <span className="text-3xl font-light tracking-wide text-white">
            rryworx
          </span>
          </Link>

          <p className="text-gray-400 text-sm leading-relaxed mb-6">
            Established software development company with offices in India &
            USA. Delivering digital transformation since 2016.
          </p>

          {/* Social Icons */}
          <div className="flex items-center gap-3">
            {[
              { icon: FaLinkedinIn, href: "https://linkedin.com" },
              { icon: FaTwitter, href: "https://twitter.com" },
              { icon: FaInstagram, href: "https://instagram.com" },
              { icon: FaWhatsapp, href: "https://wa.me/919911108696" },
            ].map(({ icon: Icon, href }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-white/15 text-gray-400 hover:border-[#355dff] hover:text-[#4da3ff] hover:bg-[#355dff]/10 transition duration-300"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>

        {/* Services */}
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-widest text-gray-300 mb-5">
            Services
          </h4>

          <ul className="space-y-3">
            {[
              { label: "App Development", to: "/services/app" },
              { label: "Web Development", to: "/services/web" },
              { label: "AI Development", to: "/services/ai" },
            ].map(({ label, to }) => (
              <li key={to} className="overflow-hidden">
                <Link
                  to={to}
                  className="text-gray-400 text-sm hover:text-[#4da3ff] transition-colors group flex items-center"
                >
                  <span className="inline-flex items-center gap-2 -translate-x-[21px] group-hover:translate-x-0 transition-transform duration-300">
                    <ArrowRight size={13} className="flex-shrink-0" />
                    <span>{label}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-widest text-gray-300 mb-5">
            Company
          </h4>

          <ul className="space-y-3">
            {[
              { label: "Home", to: "/" },
              { label: "About Us", to: "/about" },
              { label: "Portfolio", to: "/portfolio" },
            ].map(({ label, to }) => (
              <li key={to} className="overflow-hidden">
                <Link
                  to={to}
                  className="text-gray-400 text-sm hover:text-[#4da3ff] transition-colors group flex items-center"
                >
                  <span className="inline-flex items-center gap-2 -translate-x-[21px] group-hover:translate-x-0 transition-transform duration-300">
                    <ArrowRight size={13} className="flex-shrink-0" />
                    <span>{label}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-widest text-gray-300 mb-5">
            Contact
          </h4>

          <ul className="space-y-4">
            <li>
              <a
                href="https://mail.google.com/mail/?view=cm&to=info@orryworx.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-gray-400 text-sm hover:text-[#4da3ff] transition group"
              >
                <Mail
                  size={16}
                  className="mt-0.5 flex-shrink-0 text-[#355dff] group-hover:text-[#4da3ff]"
                />
                info@orryworx.com
              </a>
            </li>

            <li>
              <a
                href="tel:+919911108696"
                className="flex items-center gap-3 text-gray-400 text-sm hover:text-[#4da3ff] transition group"
              >
                <Phone
                  size={16}
                  className="flex-shrink-0 text-[#355dff] group-hover:text-[#4da3ff]"
                />
                +91 9911108696
              </a>
            </li>

            <li>
              <a
                href="https://www.orryworx.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-400 text-sm hover:text-[#4da3ff] transition group"
              >
                <Globe
                  size={16}
                  className="flex-shrink-0 text-[#355dff] group-hover:text-[#4da3ff]"
                />
                www.orryworx.com
              </a>
            </li>

            <li className="flex items-start gap-3 text-gray-400 text-sm">
              <MapPin
                size={16}
                className="mt-0.5 flex-shrink-0 text-[#355dff]"
              />
              <span>
                1038 Sector 40, Gurgaon,
                <br />
                Haryana, India – 122001
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {currentYear} Orryworx. All rights reserved.</p>

          <p className="text-center">
            Crafted with care in India 🇮🇳 &amp; USA 🇺🇸
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;